/** Opção local do Instituto Previdenciário: `id` sequencial e o texto exibido. */
export interface InstitutoOpcao {
  id: number;
  label: string;
}

export interface InstitutoStatusCor {
  cor: string;
  texto: string;
}

/** O `id` é o mesmo que a API Instituto Online devolve em `plano`. */
export const INSTITUTO_PLANOS: readonly InstitutoOpcao[] = [
  { id: 33, label: 'CESP CD' },
  { id: 23, label: 'CD CPFL' },
  { id: 26, label: 'ISA ENERGIA BRASIL PREV' },
  { id: 22, label: 'CD 2 Eletropaulo' },
];

export const INSTITUTO_TIPOS: readonly InstitutoOpcao[] = [
  { id: 1, label: 'Resgate' },
  { id: 2, label: 'Autopatrocínio' },
  { id: 3, label: 'Benefício Proporcional Diferido (BPD)' },
  { id: 4, label: 'Portabilidade' },
];

/** Status do Instituto: `valor` é o código que a API usa em `statusUnificado` (e no filtro). */
export interface InstitutoStatusOpcao extends InstitutoOpcao {
  valor: string;
}

export const INSTITUTO_STATUS: readonly InstitutoStatusOpcao[] = [
  { id: 1, valor: 'Solicitado', label: 'Solicitado' },
  { id: 2, valor: 'EmRetencao', label: 'Em retenção' },
  { id: 3, valor: 'AguardandoDocumentacao', label: 'Aguardando documentação' },
  { id: 4, valor: 'DocumentoReenviado', label: 'Documento reenviado' },
  { id: 5, valor: 'DocumentoValidado', label: 'Documento validado' },
  { id: 6, valor: 'EmProcessamento', label: 'Em processamento (Integração)' },
  { id: 7, valor: 'Concluido', label: 'Concluído' },
  { id: 8, valor: 'Cancelado', label: 'Cancelado' },
];

/**
 * Opção de `INSTITUTO_STATUS` do status, pelo código da API (`EmRetencao`) ou pela label
 * (`Em retenção`), sem diferenciar maiúsculas, acentos nem espaços nas pontas; `undefined` quando
 * o status não está na lista.
 */
export function getInstitutoStatusOpcao(status: string | null | undefined): InstitutoStatusOpcao | undefined {
  const texto = normalizarStatus(status);
  if (!texto) {
    return undefined;
  }
  return INSTITUTO_STATUS.find(
    (opcao) => normalizarStatus(opcao.valor) === texto || normalizarStatus(opcao.label) === texto,
  );
}

/** Label do status para a tela; um status fora de `INSTITUTO_STATUS` fica como veio (sem espaços nas pontas). */
export function getInstitutoStatusLabel(status: string | null | undefined): string {
  return getInstitutoStatusOpcao(status)?.label ?? (status ?? '').trim();
}

/**
 * Cor no gráfico "Adesões por status" de cada status, pelo texto de `normalizarStatus` (minúsculas,
 * sem acentos): é assim que `getInstitutoStatusCor` procura a chave.
 */
export const INSTITUTO_STATUS_CORES: Record<string, InstitutoStatusCor> = {
  solicitado: { cor: 'var(--color-yellow-v500)', texto: 'var(--color-yellow-v800, #8a6100)' },
  'em retencao': { cor: '#2f80c9', texto: '#1c5a94' },
  'aguardando documentacao': { cor: 'var(--color-red-v500)', texto: 'var(--color-red-v700)' },
  'documento reenviado': { cor: 'var(--color-red-v300)', texto: 'var(--color-red-v600)' },
  'documento validado': { cor: 'var(--color-green-v200)', texto: 'var(--color-green-v800)' },
  'em processamento (integracao)': { cor: '#2f80c9', texto: '#1c5a94' },
  concluido: { cor: 'var(--color-green-v500)', texto: 'var(--color-green-v800)' },
  cancelado: { cor: '#f0882a', texto: '#a3530b' },
};

/** Status fora da lista. */
const STATUS_COR_PADRAO: InstitutoStatusCor = { cor: 'var(--color-gray-v500)', texto: 'var(--color-gray-v800)' };

/** Cor do status (código da API ou label), sem diferenciar maiúsculas, acentos nem espaços nas pontas. */
export function getInstitutoStatusCor(status: string | null | undefined): InstitutoStatusCor {
  return INSTITUTO_STATUS_CORES[normalizarStatus(getInstitutoStatusLabel(status))] ?? STATUS_COR_PADRAO;
}

/**
 * Texto do status para comparação: sem espaços nas pontas, em minúsculas e sem acentos (a API
 * manda, por exemplo, "Concluido" para o "Concluído" de `INSTITUTO_STATUS`).
 */
export function normalizarStatus(status: string | null | undefined): string {
  return (status ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/** Label da opção com esse `id`; `undefined` quando o id não está na lista. */
export function getInstitutoOpcaoLabel(
  opcoes: readonly InstitutoOpcao[],
  id: number | null | undefined,
): string | undefined {
  return opcoes.find((opcao) => opcao.id === id)?.label;
}
